/**
 * HealthAtAGlance — iter-435h dashboard aggregator.
 *
 * Single admin dashboard card that fans out to 3 module health endpoints
 * in parallel and renders one row per module with a traffic-light chip:
 *
 *   1. Payments (7d)   — bookshop/admin/payments/traffic-light
 *   2. Social drains   — fwtv/admin/media-intel/drain-stats
 *   3. Rentals expiring - bookshop/admin/rentals/expiring
 *
 * Each row shows the top signal + a quick-nav link to the deep dive.
 * Refetches every 60 s.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Activity, CreditCard, Radio, BedDouble, ExternalLink, RefreshCw,
  AlertTriangle,
} from 'lucide-react';
import { getToken } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}` });

const HEALTH_STYLES = {
  healthy:  { label: 'Healthy',  cls: 'bg-emerald-50 text-emerald-700 border-emerald-500', dot: 'bg-emerald-500' },
  degraded: { label: 'Degraded', cls: 'bg-amber-50 text-amber-700 border-amber-500', dot: 'bg-amber-500' },
  critical: { label: 'Critical', cls: 'bg-rose-50 text-rose-700 border-rose-500', dot: 'bg-rose-500' },
  idle:     { label: 'Idle',     cls: 'bg-slate-50 text-slate-500 border-slate-300', dot: 'bg-slate-400' },
};


function summariseHealth(rows) {
  // Aggregate the worst platform status across all rows.
  const order = ['critical', 'degraded', 'healthy', 'idle'];
  const seen = new Set(rows.map(r => r.health));
  for (const level of order) if (seen.has(level)) return level;
  return 'idle';
}


export function HealthAtAGlance() {
  const payments = useQuery({
    queryKey: ['hg-payments'],
    queryFn: async () => (await axios.get(
      `${API}/api/bookshop/admin/payments/traffic-light?days=7`,
      { headers: authH() })).data,
    refetchInterval: 60000,
  });
  const drains = useQuery({
    queryKey: ['hg-drains'],
    queryFn: async () => (await axios.get(
      `${API}/api/fwtv/admin/media-intel/drain-stats?weeks=1`,
      { headers: authH() })).data,
    refetchInterval: 60000,
  });
  const rentals = useQuery({
    queryKey: ['hg-rentals'],
    queryFn: async () => (await axios.get(
      `${API}/api/bookshop/admin/rentals/expiring?within_hours=24`,
      { headers: authH() })).data,
    refetchInterval: 60000,
  });

  const refetchAll = () => {
    payments.refetch(); drains.refetch(); rentals.refetch();
  };

  // Payment summary
  const p = payments.data?.providers || [];
  const paymentHealth = summariseHealth(p);
  const worstPayment = p.filter(x => x.health !== 'idle')
    .sort((a, b) => (a.success_rate ?? 1) - (b.success_rate ?? 1))[0];
  const paymentAwaiting = payments.data?.totals?.awaiting_review || 0;

  // Drain summary
  const d = drains.data?.platforms || [];
  const drainHealth = summariseHealth(d);
  const stalePlatforms = d.filter(x => x.stale_credentials_suspected).map(x => x.platform);
  const drainFailed = drains.data?.totals?.failed || 0;
  const drainSent = drains.data?.totals?.sent || 0;

  // Rentals summary
  const r = rentals.data?.rentals || [];
  const rentalsCount = r.length;
  const rentalsHealth = rentalsCount === 0 ? 'idle' : (rentalsCount > 10 ? 'degraded' : 'healthy');

  const allLoaded = !payments.isLoading && !drains.isLoading && !rentals.isLoading;

  return (
    <Card data-testid="health-at-a-glance">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2">
          <Activity className="h-4 w-4 text-indigo-600" /> Health at a glance
          <span className="text-xs text-slate-400">· auto-refreshes every 60 s</span>
        </CardTitle>
        <Button size="sm" variant="outline" onClick={refetchAll}
                  data-testid="hg-refresh">
          <RefreshCw className={`h-3 w-3 mr-1 ${allLoaded ? '' : 'animate-spin'}`} />
          Refresh
        </Button>
      </CardHeader>
      <CardContent className="space-y-2">
        <HealthRow
          icon={<CreditCard className="h-4 w-4" />}
          label="Payments (7d)"
          health={paymentHealth}
          summary={
            paymentHealth === 'idle'
              ? 'No payments in the last 7 days.'
              : (
                <>
                  {worstPayment ? (
                    <>Worst: <strong>{worstPayment.method.replace(/_/g, ' ')}</strong>
                      &nbsp;· {worstPayment.success_rate !== null
                        ? `${Math.round(worstPayment.success_rate * 100)}% success` : 'no attempts'}
                    </>
                  ) : 'All providers healthy.'}
                  {paymentAwaiting > 0 && (
                    <> · <span className="text-amber-700"><strong>{paymentAwaiting}</strong> to review</span></>
                  )}
                </>
              )
          }
          href="/admin/shop"
          testId="hg-row-payments"
        />
        <HealthRow
          icon={<Radio className="h-4 w-4" />}
          label="Social drains (7d)"
          health={drainHealth}
          summary={
            drainSent + drainFailed === 0
              ? 'Nothing published this week.'
              : (
                <>
                  <strong className="text-emerald-700">{drainSent}</strong> sent,{' '}
                  <strong className="text-rose-700">{drainFailed}</strong> failed
                  {stalePlatforms.length > 0 && (
                    <> · <span className="text-rose-700 inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3" />stale creds: {stalePlatforms.join(', ')}</span></>
                  )}
                </>
              )
          }
          href="/admin/media-intel"
          testId="hg-row-drains"
        />
        <HealthRow
          icon={<BedDouble className="h-4 w-4" />}
          label="Rentals expiring next 24h"
          health={rentalsHealth}
          summary={
            rentalsCount === 0
              ? 'No rentals expiring in the next 24h.'
              : (
                <>
                  <strong>{rentalsCount}</strong> rental{rentalsCount === 1 ? '' : 's'} expiring —
                  {' '}sweep runs hourly.
                </>
              )
          }
          href="/admin/shop"
          testId="hg-row-rentals"
        />
      </CardContent>
    </Card>
  );
}


function HealthRow({ icon, label, health, summary, href, testId }) {
  const st = HEALTH_STYLES[health] || HEALTH_STYLES.idle;
  return (
    <div className={`border-l-4 rounded-r-md p-2 flex items-center gap-3 ${st.cls}`}
          data-testid={testId}>
      <span className={`h-2 w-2 rounded-full ${st.dot} shrink-0`} />
      <div className="text-slate-600 shrink-0">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-slate-800">{label}</div>
        <div className="text-xs text-slate-600 truncate">{summary}</div>
      </div>
      <Badge variant="outline" className={`text-[10px] shrink-0 ${st.cls}`}
                data-testid={`${testId}-badge`}>
        {st.label}
      </Badge>
      <a href={href} className="shrink-0"
          data-testid={`${testId}-link`}
          aria-label={`Open ${label} detail`}>
        <Button size="sm" variant="ghost" className="h-7 w-7 p-0">
          <ExternalLink className="h-3 w-3" />
        </Button>
      </a>
    </div>
  );
}

export default HealthAtAGlance;
